import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Link,
  Option,
  Select,
  Stack,
  Typography,
} from '@mui/joy';
import { useMutation } from '@tanstack/react-query';
import api from '../../../lib/api';
import {
  ResponsiveTable,
  type ResponsiveColumn,
} from '../../../components/common/ResponsiveTable';
import type { DomainDiagnosticsResult } from '../../../types/websiteWorkspace';
import { apiMessage } from './workspaceLogic';

const instructions: Record<string, string> = {
  godaddy:
    'GoDaddy: open your domain → DNS → Manage DNS. Paste certificate CNAME records exactly. For the root domain, use forwarding to www if your plan does not support ALIAS flattening.',
  cloudflare:
    'Cloudflare: open the zone → DNS → Records. Use DNS-only (grey cloud) for certificate validation. Cloudflare can flatten a root CNAME; use the exact target shown above.',
  namecheap:
    'Namecheap: open Domain List → Manage → Advanced DNS. Use @ for the root host and the provided CNAME values for validation and www.',
  other:
    'Open the DNS zone at your registrar. Copy each host, type and value exactly. If root ALIAS records are unsupported, connect www as primary and configure root forwarding at your provider.',
};

export default function DomainDiagnostics({
  projectId,
  domain,
  status,
}: {
  projectId: string;
  domain: string;
  status: string;
}) {
  const [registrar, setRegistrar] = useState('other');
  const check = useMutation({
    mutationFn: async () =>
      (
        await api.post<DomainDiagnosticsResult>(
          `/api/website-builder/projects/${projectId}/domain-diagnostics/`,
          {},
        )
      ).data,
  });
  const columns: ResponsiveColumn<
    DomainDiagnosticsResult['records'][number]
  >[] = [
    {
      key: 'host',
      header: 'Record',
      mobileVariant: 'header',
      render: (row) => (
        <Typography sx={{ overflowWrap: 'anywhere' }}>
          {row.type} · {row.host}
        </Typography>
      ),
    },
    {
      key: 'expected',
      header: 'Expected',
      mobileVariant: 'body',
      render: (row) => (
        <Typography level="body-sm" sx={{ overflowWrap: 'anywhere' }}>
          {row.value}
        </Typography>
      ),
    },
    {
      key: 'observed',
      header: 'Observed publicly',
      mobileVariant: 'body',
      render: (row) => (
        <Typography level="body-sm" sx={{ overflowWrap: 'anywhere' }}>
          {row.observed.join(', ') || 'Not found yet'}
        </Typography>
      ),
    },
    {
      key: 'result',
      header: 'Next action',
      render: (row) => (
        <Stack spacing={1}>
          <Chip size="sm" color={row.matched ? 'success' : 'warning'}>
            {row.matched ? 'Matches' : 'Needs attention'}
          </Chip>
          <Typography level="body-xs">{row.next_action}</Typography>
        </Stack>
      ),
    },
  ];
  return (
    <Stack spacing={2} sx={{ mt: 3 }}>
      <Typography level="title-lg">Domain launch checklist</Typography>
      <Typography level="body-sm">
        1. Add validation and pointing records → 2. Wait for the certificate →
        3. Attach hosting → 4. Verify public HTTPS.
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Chip color={status === 'live' ? 'success' : 'neutral'}>
          Hosting: {status || 'not connected'}
        </Chip>
        <Chip
          color={
            check.data?.https.status === 'reachable' ? 'success' : 'neutral'
          }
        >
          Public HTTPS: {check.data?.https.status || 'not checked'}
        </Chip>
      </Box>
      <Select
        aria-label="DNS provider guidance"
        value={registrar}
        onChange={(_, value) => setRegistrar(value || 'other')}
      >
        <Option value="other">My DNS provider</Option>
        <Option value="godaddy">GoDaddy</Option>
        <Option value="cloudflare">Cloudflare</Option>
        <Option value="namecheap">Namecheap</Option>
      </Select>
      <Typography level="body-sm">{instructions[registrar]}</Typography>
      <Button
        variant="outlined"
        loading={check.isPending}
        disabled={!domain}
        onClick={() => check.mutate()}
      >
        Check public DNS & HTTPS
      </Button>
      {check.isError && (
        <Alert color="danger">
          {apiMessage(check.error, 'The public check failed. Please retry.')}
        </Alert>
      )}
      {check.data && (
        <>
          <Typography level="body-xs">
            Last checked {new Date(check.data.checked_at).toLocaleString()}
          </Typography>
          <Alert
            color={
              check.data.https.status === 'reachable' ? 'success' : 'warning'
            }
          >
            {check.data.https.message}
          </Alert>
          <ResponsiveTable
            columns={columns}
            rows={check.data.records}
            getRowKey={(row) => `${row.type}:${row.host}`}
            emptyState={
              <Typography>No DNS records are available yet.</Typography>
            }
          />
          <Typography level="body-xs">{check.data.note}</Typography>
          {check.data.https.status === 'reachable' && (
            <Link
              href={`https://${domain}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open public site
            </Link>
          )}
        </>
      )}
    </Stack>
  );
}
